import { resolveImageUrl } from "@/lib/api";
import { QrCode } from "lucide-react";
import { fontStyleToCss, buttonStyleToCss, reconcileGrid } from "@/lib/kioskGrid";
import { FitText } from "@/components/FitText";

const fmtTime = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const fmtDate = (d) => d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

/**
 * Presentational welcome-screen renderer shared by the live kiosk and the editor preview.
 * Renders ONLY the home content (header + main + grid) — overlays, dialogs and on-kiosk
 * controls stay in the Kiosk page. When `onFocus` is provided (editor preview mode),
 * major elements become clickable to jump to their controls.
 *
 * ARCHITECTURE: this renders the shipped "interactive welcome" Experience (Connect tier).
 * A future Grow "Display Mode" Experience (passive lobby/TV signage) should be a SEPARATE
 * renderer that reuses the shared Viewport component — not a fork of this file.
 */
export function KioskCanvas({ cfg, eff, activeScreen, now, carouselIdx = 0, defaultLogo = "", onButton, onEvent, onQr, onFocus }) {
  const E = eff || cfg;
  const noop = () => {};
  const B = onButton || noop;
  const EV = onEvent || noop;
  const QR = onQr || noop;
  const clock = now || new Date();
  // Branding visibility (default true → legacy configs unchanged).
  const showLogo = cfg.show_logo !== false;
  const showBrand = cfg.show_brand_name !== false;
  const showSubtext = cfg.show_welcome_subtext !== false;
  // Experience logo inheritance: "custom" uses this experience's logo, else the church default.
  const effLogo = ((cfg.logo_mode || "custom") !== "default" && cfg.logo_url) ? cfg.logo_url : (defaultLogo || cfg.logo_url || "");

  const focusable = (key, node) =>
    onFocus ? (
      <div
        onClick={(e) => { e.stopPropagation(); onFocus(key); }}
        className="cursor-pointer rounded-xl transition hover:ring-2 hover:ring-[#2A2626]/40 hover:ring-offset-2 h-full"
        data-focus={key}
        data-testid={`preview-focus-${key}`}
      >
        {node}
      </div>
    ) : node;

  const events = (E.featured_events || []).filter((e) => e && e.visible !== false);
  const featured = E.carousel_mode && events.length > 0 ? events[carouselIdx % events.length] : events[0];
  const layout = E.layout_preset || "split_right";
  const gridMode = E.layout_mode === "grid";
  const gridItems = gridMode ? reconcileGrid(E).items : [];

  // Spotlight display mode drives the ACTUAL outer tile geometry (not just image fit).
  // AUTO = scale to fit the available area (object-contain, flexible height).
  // WIDE/SQUARE/BANNER = the tile itself takes that aspect ratio (object-cover).
  const ASPECT_MAP = { wide: "16 / 9", square: "1 / 1", banner: "1920 / 692" };
  const featMode = (featured && featured.aspect_ratio) || "auto";
  const featAspect = ASPECT_MAP[featMode];
  const featImgFit = featMode === "auto" ? "object-contain" : "object-cover";

  const FeaturedInner = featured && featured.image_url ? (
    <button
      onClick={() => EV(featured)}
      className={`kiosk-tap relative rounded-3xl overflow-hidden shadow-lg group block bg-[#111]/5 ${featAspect ? "w-full" : "w-full h-full min-h-[220px]"}`}
      style={featAspect ? { aspectRatio: featAspect, maxHeight: "100%" } : undefined}
      data-testid="kiosk-featured-event"
      data-aspect={featMode}
    >
      <img src={resolveImageUrl(featured.image_url)} alt={featured.title} className={`absolute inset-0 w-full h-full ${featImgFit}`} />
      {featured.show_text !== false && featured.title ? (<>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white text-left">
          <p className="text-[11px] uppercase tracking-widest opacity-80">This week</p>
          <h2 className="text-xl md:text-2xl font-bold mt-1">{featured.title}</h2>
        </div>
      </>) : null}
      {E.carousel_mode && events.length > 1 && (
        <div className="absolute top-4 right-4 flex gap-1">
          {events.map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full transition-all ${i === carouselIdx ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />
          ))}
        </div>
      )}
    </button>
  ) : layout !== "buttons_focus" ? (
    <div className={`w-full rounded-3xl bg-white/60 border-2 border-dashed border-[#E5E2DC] flex items-center justify-center text-[#5C5856] ${featAspect ? "" : "h-full min-h-[220px]"}`} style={featAspect ? { aspectRatio: featAspect } : undefined} data-testid="kiosk-featured-event" data-aspect={featMode}>
      No featured announcement yet
    </div>
  ) : null;

  const FeaturedBlock = FeaturedInner ? focusable("featured", FeaturedInner) : null;

  const renderKioskButton = (btn, idx, opts = {}) => (
    <div className={`flex items-stretch gap-3 ${opts.fill ? "h-full" : ""}`}>
      <button
        onClick={() => B(btn)}
        className={`kiosk-tap flex-1 text-left hover:brightness-110 ${opts.fill ? "flex items-center" : ""}`}
        style={buttonStyleToCss(btn.style || cfg.button_style)}
        data-testid={`kiosk-button-${idx}`}
      >
        <span className="flex flex-col items-start w-full min-w-0">
          <span className="w-full">{btn.label}</span>
          {btn.subtitle ? <span className="w-full text-[0.62em] font-normal opacity-80 mt-1 leading-snug normal-case">{btn.subtitle}</span> : null}
        </span>
      </button>
      {btn.qr_enabled && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); QR({ button_id: btn.id, title: btn.label, url: btn.qr_url || btn.link_value || (typeof window !== "undefined" ? window.location.href : "") }); }}
          className={`kiosk-tap rounded-2xl bg-white border border-[#E5E2DC] shadow-md flex items-center justify-center shrink-0 ${opts.fill ? "aspect-square h-full" : "aspect-square"}`}
          style={opts.fill ? {} : { width: "80px", height: "80px" }}
          aria-label={`QR for ${btn.label}`}
          data-testid={`kiosk-qr-button-${idx}`}
        >
          <QrCode className="w-7 h-7 text-[#2A2626]" />
        </button>
      )}
    </div>
  );

  const buttonsList = E.buttons || [];
  const renderButtons = (fill = false) => (
    <div className={`${layout === "buttons_focus" ? "grid grid-cols-1 sm:grid-cols-2 gap-5" : "flex flex-col gap-5"} ${fill ? "h-full" : ""}`}>
      {buttonsList.map((btn, idx) => (
        <div key={btn.id} className={fill ? "flex-1 min-h-[88px]" : ""}>{focusable("buttons", renderKioskButton(btn, idx, { fill }))}</div>
      ))}
    </div>
  );

  const LogoWidget = !showLogo ? null : effLogo ? (
    <div className="h-full flex items-center">
      <img src={resolveImageUrl(effLogo)} alt="Logo" className="max-h-full max-w-full object-contain" />
    </div>
  ) : (
    <div className="h-full flex items-center">
      <div className="h-16 w-16 rounded-xl bg-white flex items-center justify-center font-bold text-2xl border border-[#E5E2DC] shrink-0" style={{ color: cfg.accent_color }}>
        {(cfg.brand_name || "C").charAt(0)}
      </div>
    </div>
  );

  const BrandWidget = (
    <div className="h-full flex flex-col justify-center min-w-0">
      {showBrand && (
        <div className="flex-1 min-h-0">
          <FitText max={cfg.heading_font?.size || 48} min={14} style={fontStyleToCss(cfg.heading_font)}>
            {cfg.brand_name}
          </FitText>
        </div>
      )}
      {showSubtext && (
        <p className="text-sm opacity-60 mt-1 truncate shrink-0">{cfg.welcome_subtext || "Welcome — we're so glad you're here."}</p>
      )}
    </div>
  );

  const clockAlign = cfg.clock_position || "right";
  const clockItems = clockAlign === "left" ? "items-start" : clockAlign === "center" ? "items-center" : "items-end";
  const clockText = clockAlign === "left" ? "text-left" : clockAlign === "center" ? "text-center" : "text-right";
  const ClockWidget = cfg.show_datetime ? (
    <div className={`h-full w-full flex flex-col justify-center gap-1 ${clockItems}`}>
      <div className="w-full flex-[3] min-h-0">
        <FitText max={72} min={12} className={clockText} style={{ color: cfg.default_font?.color, fontWeight: 600 }}>{fmtTime(clock)}</FitText>
      </div>
      <div className="w-full flex-[2] min-h-0 opacity-70">
        <FitText max={26} min={9} className={clockText} style={{ color: cfg.default_font?.color }}>{fmtDate(clock)}</FitText>
      </div>
    </div>
  ) : null;

  const renderGridWidget = (key) => {
    if (key === "logo") return focusable("logo", LogoWidget);
    if (key === "brand") return focusable("branding", BrandWidget);
    if (key === "clock") return focusable("branding", ClockWidget);
    if (key === "featured") return <div className="h-full w-full overflow-hidden flex items-start justify-center">{FeaturedBlock}</div>;
    const allButtons = E.buttons || [];
    const bIdx = allButtons.findIndex((b, i) => (b.id || `btn_${i}`) === key);
    if (bIdx !== -1) return focusable("buttons", <div className="h-full">{renderKioskButton(allButtons[bIdx], bIdx, { fill: true })}</div>);
    return null;
  };

  if (gridMode) {
    return (
      <main className="px-6 py-6" data-testid="kiosk-grid-main">
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
            gridTemplateRows: "repeat(8, minmax(0, 1fr))",
            height: "calc(100vh - 3rem)",
          }}
        >
          {gridItems.map((it) => (
            <div
              key={it.i}
              data-testid={["logo", "brand", "clock", "featured"].includes(it.i) ? `kiosk-grid-${it.i}` : `kiosk-grid-widget-${it.i}`}
              style={{
                gridColumn: `${it.x + 1} / span ${it.w}`,
                gridRow: `${it.y + 1} / span ${it.h}`,
                minWidth: 0,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              {renderGridWidget(it.i)}
            </div>
          ))}
        </div>
      </main>
    );
  }

  return (
    <>
      {(showLogo || showBrand || showSubtext || cfg.show_datetime) && (
        <header className="px-8 pt-8 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {showLogo && focusable("logo", effLogo ? (
              <img src={resolveImageUrl(effLogo)} alt="Logo" className="h-16 w-auto max-w-[160px] object-contain" />
            ) : (
              <div className="h-16 w-16 rounded-xl bg-white flex items-center justify-center font-bold text-2xl border border-[#E5E2DC]" style={{ color: cfg.accent_color }}>
                {(cfg.brand_name || "C").charAt(0)}
              </div>
            ))}
            {(showBrand || showSubtext) && focusable("branding", (
              <div>
                {showBrand && <h1 style={fontStyleToCss(cfg.heading_font)} className="leading-none">{cfg.brand_name}</h1>}
                {showSubtext && <p className="text-sm opacity-60 mt-1">{cfg.welcome_subtext || "Welcome — we're so glad you're here."}</p>}
              </div>
            ))}
          </div>
          {cfg.show_datetime && focusable("branding", (
            <div className="text-right mr-20">
              <p className="text-2xl font-semibold" style={{ color: cfg.default_font?.color }}>{fmtTime(clock)}</p>
              <p className="text-sm opacity-70">{fmtDate(clock)}</p>
            </div>
          ))}
        </header>
      )}

      <main className="px-8 pb-8">
        {layout === "split_right" && (
          <div className="grid lg:grid-cols-5 gap-8 items-stretch lg:h-[62vh]">
            <div className="lg:col-span-2">{FeaturedBlock}</div>
            <div className="lg:col-span-3">{renderButtons(true)}</div>
          </div>
        )}
        {layout === "split_left" && (
          <div className="grid lg:grid-cols-5 gap-8 items-stretch lg:h-[62vh]">
            <div className="lg:col-span-3">{renderButtons(true)}</div>
            <div className="lg:col-span-2">{FeaturedBlock}</div>
          </div>
        )}
        {layout === "stacked" && (
          <div className="space-y-6">
            <div className="max-h-[42vh] overflow-hidden rounded-3xl">{FeaturedBlock}</div>
            <div className="max-w-4xl mx-auto w-full">{renderButtons(false)}</div>
          </div>
        )}
        {layout === "buttons_focus" && (
          <div className="max-w-5xl mx-auto pt-8">{renderButtons(false)}</div>
        )}
        {activeScreen && (
          <p className="mt-6 text-center text-xs uppercase tracking-widest opacity-50">Showing: {activeScreen.name}</p>
        )}
      </main>
    </>
  );
}
